<template>
  <div class="flex justify-content-center mt-2 mb-2">
    <span class="text-xl">{{ label }}</span>
  </div>
  <div class="flex justify-content-center mt-2 mb-2">
    <SelectButton v-model="localTimeMode" :options="this.$store.getters.getTimeType" optionLabel="name" optionValue="value" dataKey="value" aria-labelledby="name">
      <template #option="slotProps">
        <span class="flex justify-content-center mx-auto" v-tooltip="slotProps.option.name"><i :class="slotProps.option.icon" class="text-xl icon-inline"></i></span>
      </template>
    </SelectButton>
  </div>
  <div class="field">
    <div class="p-inputgroup">
      <span v-if="localTimeMode !== 0" class="p-inputgroup-addon">
        <ToggleButton v-model="localPlusMinus" onIcon="fi fi-rr-minus" offIcon="fi fi-rr-plus" />
      </span>
      <span class="p-float-label mr-2">
        <InputMask
          :id="id" 
          :ref="id" 
          v-model="localValue"
          mask="99:99:99"
          slotChar="hh:mm:ss"
          placeholder="" 
          :class="['p-inputtext-lg', { 'fg-lightCrimson': !complete || !isValid }]"
          @blur="onBlur"
          @keydown.enter="onEnter"
          @focus="focusElement"
          :disabled="disabled"
        />
        <label :for="id" class="mr-2">{{ localLabel }}</label>
        <span class="p-inputgroup-addon">{{ hourStyle }}</span>
    </span>
    </div>
  </div>
</template>

<script>
import InputMask from 'primevue/inputmask'

export default {
  name: 'TimePeriodInput',
  components: {
    InputMask
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: 'time-input'
    },
		disabled: {
			type: Boolean,
			default: false
		},
    timeMode: {
      type: Number,
      default: 0
    },
    plusMinus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'update:timeMode', 'update:plusMinus', 'valid'],
  data() {
    return {
      localValue: this.modelValue || '00:00:00',
      localTimeMode: this.timeMode || 0,
      localPlusMinus: this.plusMinus || false,
      isValid: true,
      initValue: null,
      complete: true,
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal !== this.localValue) {
        this.localValue = newVal || '00:00:00'
      }
    },
    localValue(newVal) {
      this.checkLiveInput(newVal)
      this.$emit('update:modelValue', newVal)
    },
    timeMode(newVal) {
      if (newVal !== this.localTimeMode) {
        this.localTimeMode = newVal || 0
      }
    },
    localTimeMode(newVal) {
      this.$emit('update:timeMode', newVal)
    },
    plusMinus(newVal) {
      if (newVal !== this.localPlusMinus) {
        this.localPlusMinus = newVal || 0
      }
    },
    localPlusMinus(newVal) {
      this.$emit('update:plusMinus', newVal)
    },
  },
	mounted() {
    if (!this.modelValue || this.modelValue === "") {
      this.localValue = '00:00:00'
      this.initValue = '00:00:00'
      this.$emit('update:modelValue', this.localValue)
      this.$emit('valid', true)
    } else {
      this.initValue = this.modelValue;
    }
  },
  computed: {
    localLabel() {
      if (this.localTimeMode === 0) {
        return `fixe Uhrzeit`;
      } else if (this.localTimeMode === 1) {
        if (this.localPlusMinus) return `vor Sonnenaufgang`
        else return `nach Sonnenaufgang`
      } else {
        if (this.localPlusMinus) return `vor Sonnenuntergang`
        else return `nach Sonnenuntergang`
      }
    },
    hourStyle() {
      if (this.localTimeMode === 0) return `Uhr`
      else return `Stunden`
    },
  },
  methods: {
    isInputComplete(val) {
      return /^\d{2}:\d{2}:\d{2}$/.test(val)
    },
    checkLiveInput(val) {
      const fullFormat = /^\d{2}:\d{2}:\d{2}$/.test(val)
      this.isComplete = fullFormat

      if (fullFormat) {
        this.isValid = this.isValidTime(val)
      } else {
        this.isValid = false
      }
    },
    onEnter(event) {
      event.preventDefault()
      this.onBlur()
    },
    onBlur() {
      const input = this.localValue || ''
      const isComplete = this.isInputComplete(input)

      if (!isComplete) {
        this.complete = false
        this.isValid = false
        // return
      }

      // Ensure all digits are filled — no underscores, letters, or missing digits
      const isFullyFilled = /^\d{2}:\d{2}:\d{2}$/.test(input)

      if (!isFullyFilled) {
        this.localValue = this.initValue
        this.$emit('update:modelValue', this.localValue)
        this.$emit('valid', false)
        return
      }

      const corrected = this.correctTime(this.localValue || '00:00:00')
      this.localValue = corrected
      this.isValid = this.isValidTime(corrected)
      this.complete = true
      this.$emit('update:modelValue', corrected)
      this.$emit('valid', this.isValidTime(corrected))
    },
    correctTime(val) {
      const safeVal = typeof val === 'string' ? val : '00:00:00'
      const parts = safeVal.split(':')

      if (parts.length !== 3) return '00:00:00'

      // Extract 2-digit numeric parts with fallback
      const cleanPart = (p) => {
        const digits = p.replace(/\D/g, '').padEnd(2, '0').slice(0, 2)
        return parseInt(digits, 10)
      }

      let hh = cleanPart(parts[0])
      let mm = cleanPart(parts[1])
      let ss = cleanPart(parts[2])

      // Clamp values
      if (isNaN(hh)) hh = 0
      if (isNaN(mm)) mm = 0
      if (isNaN(ss)) ss = 0

      if (hh > 24) hh = 24
      if (hh === 24 && (mm > 0 || ss > 0)) {
        hh = 24
        mm = 0
        ss = 0
      }

      if (mm > 59) mm = 59
      if (ss > 59) ss = 59

      return [
        String(hh).padStart(2, '0'),
        String(mm).padStart(2, '0'),
        String(ss).padStart(2, '0')
      ].join(':')
    },
    isValidTime(val) {
      const [hh, mm, ss] = val.split(':').map(Number)
      return (
        !isNaN(hh) && !isNaN(mm) && !isNaN(ss) &&
        hh >= 0 && hh <= 24 &&
        mm >= 0 && mm <= 59 &&
        ss >= 0 && ss <= 59 &&
        !(hh === 24 && (mm > 0 || ss > 0))
      )
    },
    focusElement(event) {
      if (event !== undefined) {
        event.target.select();
      }
    },
  }
}
</script>

<style scoped>
.p-error {
  font-size: 0.75rem;
  color: #f44336;
}

.p-togglebutton {
    font-size: 0.8rem;
    line-height: 1rem;
    padding: 0.2rem;
    margin: 0;
}
</style>